// src/pages/Login.tsx
import React, { useState } from "react";
import { Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import http from "../services/http";
import { useSnackbar } from "../contexts/SnackbarContext";

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { notify } = useSnackbar();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!username || !password) {
      setFieldError("请输入用户名和密码");
      return;
    }
    setFieldError(null);
    setLoading(true);
    try {
      const response = await http.post(
        "users/login",
        { username, password },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.code === "000000") {
        login(response.data.data.accessToken);
        navigate("/music");
        return;
      }
      notify(response.data.message || "登录失败，请检查用户名和密码", "error");
    } catch (error: any) {
      // 后端在部分环境下只接受 form-urlencoded，格式类错误时回退重试
      const status = error?.response?.status;
      if (status === 400 || status === 415 || status === 406) {
        try {
          const form = new URLSearchParams();
          form.set("username", username);
          form.set("password", password);
          const response2 = await http.post("users/login", form, {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          });
          if (response2.data.code === "000000") {
            login(response2.data.data.accessToken);
            navigate("/music");
            return;
          }
          notify(response2.data.message || "登录失败，请检查用户名和密码", "error");
        } catch (err2: any) {
          notify(err2?.response?.data?.message || "登录失败，请检查用户名和密码", "error");
        }
      } else if (error.response) {
        notify(`登录失败：${error.response.data?.message || error.response.statusText}`, "error");
      } else {
        notify("网络连接失败，请检查网络后重试", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "background.default" }}>
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          px: 8,
          bgcolor: "#1B1E27",
          color: "#F5F3EE",
        }}
      >
        <GraphicEqRoundedIcon sx={{ fontSize: 40, color: "primary.light", mb: 3 }} />
        <Typography variant="h3" sx={{ mb: 2 }}>
          Luna
        </Typography>
        <Typography sx={{ color: "rgba(245,243,238,0.7)", maxWidth: 360, lineHeight: 1.7 }}>
          音乐内容管理后台——上传曲目、维护艺术家与资讯，供 Luna 全平台客户端调用。
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            登录
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            使用管理员账号登录以继续
          </Typography>
          <TextField
            label="用户名"
            fullWidth
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyDown}
            error={!!fieldError}
          />
          <TextField
            label="密码"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            error={!!fieldError}
            helperText={fieldError}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "inherit" }} /> : "登录"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
